import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  MenuItem,
  Box,
  FormControl,
  InputLabel,
  Select,
  CircularProgress
} from '@mui/material';
import api from '../services/api';

const ProjectForm = ({ open, onClose, onSubmit, initialData, isEditing = false }) => {
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    priority: '',
    responsible: '',
    startDate: '',
    endDate: '',
  });
  const [responsibles, setResponsibles] = useState([]);
  const [loadingResponsibles, setLoadingResponsibles] = useState(false);

  useEffect(() => {
    if (initialData && open) {
      setFormData({
        name: initialData.project_name || '',
        description: initialData.description || '',
        priority: initialData.priority || '',
        responsible: initialData.responsible || '',
        startDate: initialData.start_date || '',
        endDate: initialData.end_date || '',
      });
    } else if (!open) {
      // Reset form when dialog closes
      setFormData({
        name: '',
        description: '',
        priority: '',
        responsible: '',
        startDate: '',
        endDate: '',
      });
    }
  }, [initialData, open]);

  // Buscar responsáveis técnicos
  useEffect(() => {
    if (open) {
      fetchResponsibles();
    }
  }, [open]);

  const fetchResponsibles = async () => {
    try {
      setLoadingResponsibles(true);
      const response = await api.get('/technical-responsibles/');
      // Filtramos apenas responsáveis ativos
      const activeResponsibles = response.data.filter(resp => resp.is_active);
      setResponsibles(activeResponsibles);
    } catch (error) {
      console.error('Erro ao buscar responsáveis técnicos:', error);
    } finally {
      setLoadingResponsibles(false);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{isEditing ? 'Editar Projeto' : 'Criar Novo Projeto'}</DialogTitle>
      <form onSubmit={handleSubmit}>
        <DialogContent>
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
            <TextField
              label="Nome do Projeto"
              name="name"
              value={formData.name}
              onChange={handleChange}
              required
              fullWidth
            />
            
            <TextField
              label="Descrição"
              name="description"
              value={formData.description}
              onChange={handleChange}
              required
              multiline
              rows={3}
              fullWidth
            />
            
            <TextField
              select
              label="Ordem de Prioridade"
              name="priority"
              value={formData.priority}
              onChange={handleChange}
              required
              fullWidth
            >
              <MenuItem value="Alta">Alta</MenuItem>
              <MenuItem value="Média">Média</MenuItem>
              <MenuItem value="Baixa">Baixa</MenuItem>
            </TextField>
            
            <FormControl fullWidth required>
              <InputLabel id="responsible-select-label">Responsável</InputLabel>
              <Select
                labelId="responsible-select-label"
                name="responsible"
                value={formData.responsible}
                onChange={handleChange}
                label="Responsável"
                disabled={loadingResponsibles}
              >
                {loadingResponsibles ? (
                  <MenuItem disabled>
                    <CircularProgress size={20} sx={{ mr: 1 }} /> Carregando...
                  </MenuItem>
                ) : (
                  responsibles.map(resp => (
                    <MenuItem key={resp.responsible_id} value={resp.full_name}>
                      {resp.full_name} {resp.department ? `(${resp.department})` : ''}
                    </MenuItem>
                  ))
                )}
                {!loadingResponsibles && responsibles.length === 0 && (
                  <MenuItem disabled>Nenhum responsável técnico cadastrado</MenuItem>
                )}
              </Select>
            </FormControl>
            
            <TextField
              label="Data de Início"
              name="startDate"
              type="date"
              value={formData.startDate}
              onChange={handleChange}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
            
            <TextField
              label="Data de Término"
              name="endDate"
              type="date"
              value={formData.endDate}
              onChange={handleChange}
              required
              fullWidth
              InputLabelProps={{ shrink: true }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={onClose}>Cancelar</Button>
          <Button type="submit" variant="contained" color="primary">
            {isEditing ? 'Salvar Alterações' : 'Criar Projeto'}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default ProjectForm; 